import { formatDate, formatNumber } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ControlsOf, ValuesOf } from '@ngneat/reactive-forms';
import { combineLatest, lastValueFrom, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AanAfwezigheid } from '../afas-classes/aanafwezigheid.class';
import { Hourtype } from '../afas-classes/hourtype';
import { FgWeekDay } from '../classes/fg.weekday.class';
import { Realisation, RealisationHourtype } from '../classes/realisation';
import { Settlement, SettlementHourtype } from '../classes/settlement.class';
import { DefaultStand, User } from '../classes/user.class';
import { Week } from '../classes/week';
import { WeekDay } from '../classes/weekday';
import { MinutesPipe } from '../pipes/minutes.pipe';
import { HOUR_MINUTES, Utils, WORK_DAY_MINUTES } from '../utils.class';
import { ApiService } from './api/api.service';
import { ConfirmDialogService } from './confirm-dialog-service/confirm-dialog.service';
import { EmployeeService } from './employee.service';
import { LocationService } from './location.service';

export const CONTRACT_HOURTYPE_CODE_WORK_REGULAR = 'GU';
export const CONTRACT_HOURTYPE_CODES_WORK = ['2 ', CONTRACT_HOURTYPE_CODE_WORK_REGULAR];
export const CONTRACT_HOURTYPE_CODE_SICK = 'Z';
export const CONTRACT_HOURTYPE_CODE_LEAVE = 'V';
export const CONTRACT_HOURTYPE_CODE_EDUCATION = 'S';
export const CONTRACT_HOURTYPE_CODE_VERLOF = 'Verlof';
export const CONTRACT_HOURTYPE_CODE_VERZUIM = 'Verzuim';
export const CONTRACT_HOURTYPE_CODE_BANK_HOLIDAY = 'F';
export const CONTRACT_HOURTYPE_CODE_SHORT_ABSENCE = 'KV';
export const CONTRACT_HOURTYPE_CODE_TIME_FOR_TIME = 'TO';

export const SUPPLEMENT_CAO_HOURS_TEXT = 'Aanvullen tot 8 uur (CAO)';

export const NON_TIME_HOURTYPES = ['CRW'];

/**
 * Overwerkcodes en hun toeslagvariant. Als overuren van een andere dag worden gebruikt om een
 * tekortdag aan te vullen, blijft de toeslag op de oorspronkelijke dag staan onder deze code.
 */
export const INCREASE_OVERTIME_MAP = new Map<string, string>([
    ['200', '200V'],
    ['150', '150V'],
    ['145', '145V'],
    ['135', '135V'],
    ['130', '130V'],
]);

/**
 * Het percentage dat bij een toeslagcode hoort, afgeleid uit {@link INCREASE_OVERTIME_MAP} zodat
 * de koppeling tussen code en percentage maar op één plek staat. Een code die hier niet in
 * voorkomt (bijv. `GU`) heeft geen toeslag en levert daarom 100 op.
 */
const OVERTIME_PERCENTAGE_BY_BONUS_CODE = new Map<string, number>(
    [...INCREASE_OVERTIME_MAP].map(([code, bonusCode]) => [bonusCode, +code])
);

export function overtimePercentageOfBonusCode(bonusCode: string) {
    return OVERTIME_PERCENTAGE_BY_BONUS_CODE.get(bonusCode) ?? 100;
}

export const CONTRACT_HOURTYPE_CODES = [
    ...CONTRACT_HOURTYPE_CODES_WORK,
    'MU',
    'RV',
    CONTRACT_HOURTYPE_CODE_LEAVE,
    CONTRACT_HOURTYPE_CODE_SICK,
    'Vorst',
    CONTRACT_HOURTYPE_CODE_EDUCATION,
    'SD',
    'SDU',
    CONTRACT_HOURTYPE_CODE_BANK_HOLIDAY,
    'AG',
    'G',
    CONTRACT_HOURTYPE_CODE_SHORT_ABSENCE,
    'OU',
    'OUO',
    CONTRACT_HOURTYPE_CODE_TIME_FOR_TIME,
    'SG',
    'SU',
    'T',
    'TO',
    'NV',
    'B2022',
    'A',
    'F',
    'OB',
    'BV',
    'OSV',
];

@Injectable({ providedIn: 'root' })
export class SettlementsService {
    /** Proefverrekening: `saveSettlements()` slaat niets op en laat de formulieren ongemoeid. */
    dryRun = false;

    private locationService = inject(LocationService);
    private confirmDialogService = inject(ConfirmDialogService);
    private employeeService = inject(EmployeeService);

    protected apiService = inject(ApiService);

    deleteSettlements(id: number) {
        return this.apiService.deleteCall$(`settlement/${id}`);
    }

    getSettlements(beginDate: Date, endDate: Date, type?: 'entities' | 'users', afas_project_id?: string) {
        let params = {
            beginDate: new Date(beginDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
            type,
        } as any;
        if (afas_project_id) {
            params = { ...params, afas_project_id };
        }
        return this.apiService.getCall$<Settlement[]>(`settlement/list`, params);
    }

    getSettlementCounts(beginDate: Date, endDate: Date, type?: 'entities' | 'users', afas_project_id?: string) {
        let params = {
            beginDate: new Date(beginDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
            type,
        } as any;
        if (afas_project_id) {
            params = { ...params, afas_project_id };
        }
        return this.apiService.getCall$<SettlementCounts[]>(`settlement/counts`, params);
    }

    getUserSettlements(beginDate: Date, endDate: Date, userId: number) {
        const params = {
            beginDate: formatDate(new Date(beginDate), 'yyyy-MM-dd', 'nl'),
            endDate: formatDate(new Date(endDate), 'yyyy-MM-dd', 'nl'),
        } as any;
        return this.apiService.getCall$<Settlement[]>(`settlement/period/${userId}`, params);
    }

    getSettlementsTill(endDate: Date) {
        const params = {
            endDate: new Date(endDate).toISOString(),
        } as any;
        return this.apiService.getCall$<Settlement[]>(`settlement/till`, params);
    }

    changeProject(settlements: Settlement[], projectId: string) {
        const settlementsIds = settlements.map(settlement => settlement.id);
        return this.apiService.postCall$<Settlement[]>('settlement/change-project', { settlementsIds, projectId });
    }

    save(settlement: Settlement) {
        return this.apiService.postCall$<Settlement>('settlement', settlement);
    }

    saveMulti(settlements: Settlement[], bookdate: Date, userId: number) {
        let total = 0;

        settlements.forEach(s => {
            if (!NON_TIME_HOURTYPES.includes(s.hourtype_code)) {
                const decimal = Math.ceil((s.minutes * 100) / 60) / 100;

                s.amount = `${decimal}`;

                total += decimal;
            } else {
                s.amount = `${s.minutes}`;
            }
        });

        const normalSettlements = settlements.filter(s => ['GU', 'RV', 'V', 'Z', 'Vorst'].includes(s.hourtype_code));

        const totalDecimal = normalSettlements.reduce((sum, current) => sum + +current.amount, 0);

        const mod = (totalDecimal * 100) % 25;

        if (mod > 0) {
            const largestSettlement = normalSettlements
                .filter(s => !NON_TIME_HOURTYPES.includes(s.hourtype_code))
                .sort((a, b) => b.minutes - a.minutes)[0];

            largestSettlement.amount = `${+largestSettlement.amount - (totalDecimal * 100 - (totalDecimal * 100 - mod)) / 100}`;
        }

        return this.apiService.postCall$<Settlement[]>('settlement/multi', {
            settlements,
            bookdate,
            userId,
        });
    }

    deleteDay(bookdate: Date, userId: number) {
        return this.apiService.deleteCall$<boolean>('settlement', {
            bookdate: formatDate(bookdate, 'yyyy-MM-dd', 'nl'),
            userId,
        });
    }

    periodSettlementAllPdf(year: number, period: number) {
        return this.apiService.getCall$<string>(`settlement/periodSettlementAllPdf/${year}/${period}`);
    }

    periodSettlementSinglePdf(year: number, period: number, userId: number) {
        return this.apiService.getCall$<string>(`settlement/periodSettlementSinglePdf/${year}/${period}/${userId}`);
    }

    sendSingle(settlement: Settlement) {
        return this.apiService.postCall$<Settlement>('settlement/send', settlement);
    }

    getHourtypes(leave_selectable?: boolean) {
        let params = null;
        if (leave_selectable) {
            params = { leave_selectable: true };
        }
        return this.apiService.getCall$<Hourtype[]>('settlement/hourtypes', params);
    }

    dayHasRealisations(day: WeekDay, realisations: Realisation[]) {
        return realisations.some(r => Utils.dateString(r.bookdate) === Utils.dateString(day.date));
    }

    async settleBouwCaoWeekHours(
        week: Week,
        employee: User,
        fcHourTypeMap: Map<string, string>,
        realisations: Realisation[],
        hourTypes: Hourtype[],
        sleepOrLeaveHourCodes: Set<string>,
        updateForm?: (settlements: Settlement[], day: WeekDay) => void
    ) {
        console.info('bouw CAO, aanvullen tot 8 uur');

        const userEndDate = new Date(employee.enddate);

        if (userEndDate.getHours() <= 2) {
            userEndDate.setHours(userEndDate.getHours() - 3);
        }

        const totalNormalMinutesWeek = this.calculateTotalNormalMinutesThisWeek(week);
        const contractMinutes = employee.contract_hours * HOUR_MINUTES;

        if (totalNormalMinutesWeek >= contractMinutes) {
            return;
        }

        console.info(`minder dan ${contractMinutes} minuten deze week dus aanvullen cao`);

        await Promise.all(
            week.weekDays.map(async day => {
                /**
                 * Alleen aanvullen zolang de medewerker in dienst is. Een contract kan ook
                 * halverwege de maand beginnen, dus naast de einddatum ook de begindatum
                 * controleren: dagen vóór de contractstart mogen geen CAO-uren krijgen.
                 */
                const startedBeforeDay =
                    !employee.begindate || Utils.setTime(new Date(employee.begindate), 0, 0) <= day.date;
                const contractNotEnded = !employee.enddate || new Date(userEndDate) >= day.date;

                const userEmployed = contractNotEnded && startedBeforeDay;

                if (userEmployed === false) {
                    return;
                }

                this.settleCaoHours(day, week, realisations, employee, hourTypes, sleepOrLeaveHourCodes, true);

                day.settling = true;

                try {
                    await this.saveSettlements(day, employee.id, fcHourTypeMap, updateForm);
                } finally {
                    day.settling = false;
                }
            })
        );
    }

    /**
     * Vult dagen met te weinig contracturen aan met overuren van andere dagen in dezelfde week.
     *
     * Geldt voor de cao Beroepsgoederenvervoer (chauffeurs): zij krijgen uren niet gratis
     * aangevuld tot 8 uur per dag zoals in de cao Bouw & Infra, maar de tekortdagen worden
     * gevuld met overuren die elders in de week gemaakt zijn. Het goedkoopste overwerktarief
     * wordt als eerste aangesproken.
     *
     * De afgenomen overuren blijven op hun eigen dag staan als toeslagregel (bijv. `130` wordt
     * `130V`): het tarief boven de 100% wordt nog wel uitbetaald, de basisuren verhuizen naar de
     * tekortdag als GU.
     */
    async settleChauffeurShortages(
        week: Week,
        employee: User,
        fcHourTypeMap: Map<string, string>,
        hourTypes: Hourtype[],
        overtimeHourTypeCodes: string[],
        updateForm?: (settlements: Settlement[], day: WeekDay) => void
    ) {
        // Als chauffeur en uren tekort, pakken van andere dag in de week goedkoopste uurtype
        const contractMinutesDay = (employee.contract_hours / (employee.contract_hours / 8)) * HOUR_MINUTES;

        const daysWithShortage = week.weekDays.filter(
            d =>
                this.calculateContractMinutesOfDay(d) < contractMinutesDay && d.date.getDay() > 0 && d.date.getDay() < 6
        );

        for (const dayWithShortage of daysWithShortage) {
            await this.settleChauffeurShortageDay(
                week,
                dayWithShortage,
                contractMinutesDay,
                employee,
                fcHourTypeMap,
                hourTypes,
                overtimeHourTypeCodes,
                updateForm
            );
        }
    }

    private calculateContractMinutesOfDay(day: WeekDay) {
        return day.settlements
            .filter(s => CONTRACT_HOURTYPE_CODES.includes(s.hourtype_code))
            .reduce((sum, current) => sum + current.minutes, 0);
    }

    /**
     * Alle gewerkte minuten van een dag: contracturen plus overwerk. Toeslagregels (`130V` e.d.)
     * tellen niet mee, die staan naast een uurregel voor dezelfde minuten.
     */
    private calculateWorkedMinutesOfDay(day: WeekDay) {
        return day.settlements
            .filter(s => !NON_TIME_HOURTYPES.includes(s.hourtype_code) && !/^\d+V$/.test(s.hourtype_code))
            .reduce((sum, current) => sum + current.minutes, 0);
    }

    private async settleChauffeurShortageDay(
        week: Week,
        dayWithShortage: WeekDay,
        contractMinutesDay: number,
        employee: User,
        fcHourTypeMap: Map<string, string>,
        hourTypes: Hourtype[],
        overtimeHourTypeCodes: string[],
        updateForm?: (settlements: Settlement[], day: WeekDay) => void
    ) {
        const totalNormalMinutes = this.calculateContractMinutesOfDay(dayWithShortage);

        let contractMinutesToFew = contractMinutesDay - totalNormalMinutes;

        if (contractMinutesToFew <= 0) {
            return;
        }

        const daysUsed = [] as WeekDay[];

        for (const day of week.weekDays) {
            let someChange = false;

            if (day === dayWithShortage) {
                // Een tekortdag kan zichzelf niet aanvullen.
                continue;
            }

            /**
             * Alleen de uren bóven de contracturen van een dag mogen verhuizen. Anders zou een dag
             * die zelf te weinig uren heeft leeggetrokken worden, of zouden er uren ontstaan die
             * in de week niet gewerkt zijn.
             */
            let minutesAvailable = Math.max(0, this.calculateWorkedMinutesOfDay(day) - contractMinutesDay);

            day.settlements
                .filter(s => overtimeHourTypeCodes.includes(s.hourtype_code) && !s.hourtype_code.includes('Z'))
                .sort((a, b) => {
                    const aa = `${a.hourtype_code.replace('Z', '')}`;
                    const bb = `${b.hourtype_code.replace('Z', '')}`;

                    return aa.localeCompare(bb);
                })
                .forEach(settlement => {
                    if (contractMinutesToFew > 0 && minutesAvailable > 0) {
                        const subtract = Math.min(contractMinutesToFew, settlement.minutes, minutesAvailable);

                        if (subtract > 0) {
                            minutesAvailable -= subtract;

                            someChange = true;

                            settlement.minutes = settlement.minutes - subtract;

                            settlement.description = `${settlement.description} - ${new MinutesPipe().transform(subtract)} voor ${formatDate(dayWithShortage.date, 'd MMM yyyy', 'nl')}`;

                            contractMinutesToFew -= subtract;

                            // convert overtime 1xx % to xx % and let alone on the same day
                            const overtimeSettlement = new Settlement();

                            overtimeSettlement.user_id = employee.id;
                            overtimeSettlement.bookdate = settlement.bookdate;
                            overtimeSettlement.minutes = subtract;
                            overtimeSettlement.hourtype_code =
                                INCREASE_OVERTIME_MAP.get(settlement.hourtype_code) ?? settlement.hourtype_code;

                            overtimeSettlement.hourtype = hourTypes.find(
                                h => h.code === overtimeSettlement.hourtype_code
                            );

                            overtimeSettlement.description = settlement.description;

                            day.settlements.push(overtimeSettlement);
                        }
                    }
                });

            if (someChange) {
                daysUsed.push(day);

                day.settling = true;

                try {
                    await this.saveSettlements(day, employee.id, fcHourTypeMap, updateForm);
                } finally {
                    day.settling = false;
                }
            }
        }

        if (!daysUsed.length) {
            return;
        }

        const supplementMinutes = contractMinutesDay - totalNormalMinutes - contractMinutesToFew;

        let description = 'Aanvullen tot 8 uur met overuren van ';

        daysUsed.forEach((du, i) => {
            description += ` ${formatDate(du.date, 'EEEE', 'nl')}`;

            if (i < daysUsed.length - 1) {
                description += ',';
            }
        });

        if (supplementMinutes <= 0) {
            return;
        }

        /**
         * Hier ontstaat een nieuwe settlement omdat de aanvulling geen bestaande regel is die we
         * kunnen aanpassen: de overuren zijn hierboven van hun eigen dag afgehaald (die regels
         * zijn verlaagd) en moeten nu als contracturen op de tekortdag verschijnen. Dat is per
         * definitie een nieuwe regel op een andere bookdate.
         *
         * Altijd GU en nooit MU: deze functie vult een dag aan tot maximaal de contracturen van
         * die dag. Meerwerk bestaat juist alleen boven de contracturen, dus dat kan hier niet
         * ontstaan. Een chauffeur met een contract van 40 uur moet 40 contracturen in de week
         * hebben, met 32 uur contract 32 contracturen.
         */
        const settlement = new Settlement();
        settlement.user_id = employee.id;
        settlement.bookdate = formatDate(dayWithShortage.date, 'yyyy-MM-dd', 'nl') as any;
        settlement.minutes = supplementMinutes;
        settlement.hourtype_code = CONTRACT_HOURTYPE_CODE_WORK_REGULAR;
        settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);
        settlement.description = description;
        dayWithShortage.settlements.push(settlement);
        dayWithShortage.settling = true;

        try {
            await this.saveSettlements(dayWithShortage, employee.id, fcHourTypeMap, updateForm);
        } finally {
            dayWithShortage.settling = false;
        }
    }

    private calculateTotalNormalMinutesThisWeek(week: Week) {
        return week.weekDays
            .map(d =>
                d.settlements
                    .filter(s => CONTRACT_HOURTYPE_CODES.includes(s.hourtype_code))
                    .reduce((sum, current) => sum + current.minutes, 0)
            )
            .reduce((sum, current) => sum + current, 0);
    }

    settleCaoHours(
        day: WeekDay,
        week: Week,
        realisations: Realisation[],
        employee: User,
        hourTypes: Hourtype[],
        sleepOrLeaveHourCodes: Set<string>,
        final = false
    ) {
        const employeeContractStart = Utils.setTime(new Date(employee.begindate), 0, 0, 0);

        const employeeContractNotStarted = Utils.getTimeOrNull(employeeContractStart) > Utils.getTimeOrNull(day.date);

        if (employeeContractNotStarted) {
            return [];
        }

        const caoSettlements: Settlement[] = [];

        const settlements = day.settlements;

        const totalMinutesDay = settlements
            .filter(s =>
                [
                    ...sleepOrLeaveHourCodes.values(),
                    'MU',
                    'GU',
                    'V',
                    'Z',
                    'Vorst',
                    'S',
                    'SP',
                    'G',
                    'RV',
                    'SDU',
                    'F',
                    'KV',
                    'OB',
                    'BV',
                    'OSV',
                    '150Z',
                    '200Z',
                ].includes(s.hourtype_code)
            )
            .map(s => s.minutes)
            .reduce((sum, current) => sum + current, 0);

        if (!employee.overtime && (totalMinutesDay > 0 || final)) {
            const contractMinutes = employee.contract_hours * HOUR_MINUTES;

            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

            const totalNormalMinutesThisWeek = this.calculateTotalNormalMinutesThisWeek(week);

            const minDayHours = final
                ? Math.min(WORK_DAY_MINUTES, contractMinutes - totalNormalMinutesThisWeek)
                : WORK_DAY_MINUTES;

            if (
                (!isWeekend || (isWeekend && totalMinutesDay > 0)) &&
                totalMinutesDay < WORK_DAY_MINUTES &&
                minDayHours > 0
            ) {
                // console.info(`op ${day.date.getDate()} maximaal ${minDayHours} minuten toevoegen`);

                const afasProjectId = this.getLargestSettlementProject(settlements, realisations);

                // Bepaal het aanvul uurtype op basis van dag van de week
                let supplementHourType = totalNormalMinutesThisWeek >= contractMinutes ? 'MU' : 'GU'; // standaard voor weekdagen

                // Voor weekenden, vind het uurtype van de eerste settlement van de dag
                if (day.date.getDay() === 0 || day.date.getDay() === 6) {
                    const firstSettlement = settlements
                        .map(s => {
                            s.realisation = realisations.find(r => r.id === s.realisation_id);
                            return s;
                        })
                        .filter(s => !!s.realisation && s.hourtype_code !== 'GU')
                        .filter(s =>
                            [
                                RealisationHourtype.loading_unloading,
                                RealisationHourtype.driving_to,
                                RealisationHourtype.worktime,
                                RealisationHourtype.driving_back,
                                RealisationHourtype.loading_unloading_end,
                            ].includes(s.realisation.hourtype)
                        )
                        .sort(
                            (a, b) =>
                                Utils.getTimeOrNull(a.realisation.begindate) -
                                Utils.getTimeOrNull(b.realisation.begindate)
                        )[0];

                    if (firstSettlement) {
                        supplementHourType = firstSettlement.hourtype_code;
                    }
                }

                const settlement = new Settlement();

                settlement.user_id = employee.id;

                settlement.bookdate = formatDate(day.date, 'yyyy-MM-dd', 'nl') as any;

                settlement.minutes = Math.min(minDayHours, WORK_DAY_MINUTES - totalMinutesDay);

                // console.info(`${settlement.minutes} minuten toevoegen`);

                settlement.hourtype_code = supplementHourType;

                settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

                settlement.description = SUPPLEMENT_CAO_HOURS_TEXT;

                settlement.afas_project_id = afasProjectId;

                caoSettlements.push(settlement);

                const lastSettlement = settlements
                    .map(s => {
                        s.realisation = realisations.find(r => r.id === s.realisation_id);
                        return s;
                    })
                    .filter(s => !!s.realisation && !['GU', 'MU'].includes(s.hourtype_code))
                    .filter(s =>
                        [
                            RealisationHourtype.loading_unloading,
                            RealisationHourtype.driving_to,
                            RealisationHourtype.worktime,
                            RealisationHourtype.driving_back,
                            RealisationHourtype.loading_unloading_end,
                        ].includes(s.realisation.hourtype)
                    )
                    .sort(
                        (a, b) =>
                            Utils.getTimeOrNull(b.realisation.enddate) - Utils.getTimeOrNull(a.realisation.enddate)
                    )[0];

                if (lastSettlement) {
                    // Alleen de lastSettlement logica toepassen voor weekdagen (opslag) en MU
                    const createBonusSettlement = () => {
                        const settlement = new Settlement();

                        settlement.user_id = employee.id;

                        settlement.bookdate = formatDate(day.date, 'yyyy-MM-dd', 'nl') as any;

                        settlement.minutes = minDayHours - totalMinutesDay;

                        settlement.hourtype_code = lastSettlement.hourtype_code;

                        settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

                        settlement.description = SUPPLEMENT_CAO_HOURS_TEXT;

                        caoSettlements.push(settlement);
                    };

                    if (day.date.getDay() > 0 && day.date.getDay() < 6) {
                        const endDate = new Date(lastSettlement.realisation.enddate);

                        const startEarly = Utils.setTime(new Date(endDate), 0, 0);
                        const endEarly = Utils.setTime(new Date(endDate), 6, 0);

                        if (endDate.getTime() < endEarly.getTime() && endDate.getTime() > startEarly.getTime()) {
                            createBonusSettlement();
                        }
                    } else if (supplementHourType === 'MU') {
                        createBonusSettlement();
                    }
                }
            }
        }

        day.settlements.push(...caoSettlements);
    }

    getLargestSettlementProject(settlements: Settlement[], realisations: Realisation[]) {
        const ordered = settlements
            .filter(s => {
                s.realisation = realisations.find(r => r.id === s.realisation_id);

                return !!s.realisation && !!Utils.realisationAfasProjectId(s.realisation);
            })
            .sort((a, b) => b.minutes - a.minutes);

        if (ordered?.length) {
            return Utils.realisationAfasProjectId(ordered[0].realisation);
        }
    }

    async settleLeaveDays(
        day: WeekDay,
        aanAfwezigheid: AanAfwezigheid[],
        hourTypes: Hourtype[],
        fcHourTypeMap: Map<string, string>,
        employeeId: number,
        sleepOrLeaveHourCodes: Set<string>,
        updateForm?: (settlements: Settlement[], day: WeekDay) => void
    ) {
        const realisations = day.realisations;

        day.settlements = [
            // Ziekte uren
            { types: [RealisationHourtype.illness], code: CONTRACT_HOURTYPE_CODE_SICK },
            // Vrije dagen en betaald verlof
            {
                types: [RealisationHourtype.day_off, RealisationHourtype.paid_leave],
                code: CONTRACT_HOURTYPE_CODE_LEAVE,
            },
            // Kort verzuim
            { types: [RealisationHourtype.short_absence], code: CONTRACT_HOURTYPE_CODE_SHORT_ABSENCE },
            // Tijd voor tijd
            { types: [RealisationHourtype.time_for_time], code: CONTRACT_HOURTYPE_CODE_TIME_FOR_TIME },
            // Spaaruren
            { types: [RealisationHourtype.frost], code: SettlementHourtype.frost },
            // Aanvullend geboorteverlof + ouderschapsverlof
            { types: [RealisationHourtype.additional_parental], code: SettlementHourtype.additional_parental },
            // Bijzonder verlof
            { types: [RealisationHourtype.special_leave], code: SettlementHourtype.special_leave },
            // Feestdagen
            { types: [RealisationHourtype.bank_holiday], code: CONTRACT_HOURTYPE_CODE_BANK_HOLIDAY },
        ].flatMap(t => this.settleRealisationsForHourTypes(realisations, t.types, t.code, hourTypes));

        this.handleVrijInAfas(
            day,
            day.settlements,
            aanAfwezigheid,
            hourTypes,
            fcHourTypeMap,
            employeeId,
            sleepOrLeaveHourCodes
        );

        await this.saveSettlements(day, employeeId, fcHourTypeMap, updateForm);
    }

    settleEducation(
        settlements: Settlement[],
        realisations: Realisation[],
        hourCode: string,
        formValues: ValuesOf<ControlsOf<ControlsOf<FgWeekDay>>>,
        hourTypes: Hourtype[]
    ) {
        const educationSettlements: Settlement[] = [];

        const education = realisations.filter(r => [RealisationHourtype.education].includes(r.hourtype));

        /** Education hours, in minutes */
        const totalEducation = education
            .map(e => Utils.minuteDuration(new Date(e.enddate), new Date(e.begindate)))
            .reduce((sum, current) => sum + current, 0);

        education.forEach(realisation => {
            const beginDate = new Date(realisation.begindate);
            const endDate = new Date(realisation.enddate);

            const minutes = Utils.minuteDuration(endDate, beginDate);

            let settledMinutes =
                settlements.map(s => s.minutes).reduce((sum, current) => sum + current, 0) - realisation.pause;

            const tMinutes = settledMinutes + minutes;

            let aboveContract = 0;
            let normal = minutes;

            const contractHours = formValues.contractHours + realisation.pause;

            if (totalEducation >= contractHours) {
                settledMinutes = settledMinutes - formValues.pause;
            }

            if (tMinutes > formValues.contractHours || formValues.contractHours === 0) {
                const minutesInContractLeft = Math.max(contractHours - settledMinutes, 0);

                aboveContract = minutes - minutesInContractLeft;

                normal = minutesInContractLeft;
            }

            if (normal) {
                educationSettlements.push(
                    this.createSettlementFromRealisation(
                        realisation,
                        hourTypes,
                        normal,
                        CONTRACT_HOURTYPE_CODE_EDUCATION,
                        beginDate,
                        endDate
                    )
                );
            }

            if (aboveContract) {
                educationSettlements.push(
                    this.createSettlementFromRealisation(
                        realisation,
                        hourTypes,
                        aboveContract,
                        hourCode,
                        beginDate,
                        endDate
                    )
                );
            }
        });

        return educationSettlements;
    }

    settlePauzes(day: WeekDay, pauseToSettle: number, hourTypes: Hourtype[]) {
        const normalHours = [CONTRACT_HOURTYPE_CODE_WORK_REGULAR, 'MU'];

        const nonPauseHourtypes = [
            'SDU',
            'SD',
            'SDW',
            ...hourTypes
                .filter(ht => [CONTRACT_HOURTYPE_CODE_VERZUIM, CONTRACT_HOURTYPE_CODE_VERLOF].includes(ht.type))
                .map(ht => ht.code),
        ];

        day.settlements
            .filter(s => !s.bonus && !nonPauseHourtypes.includes(s.hourtype_code) && !s.skip_pause)
            .sort((a, b) => this.pauseSortKey(a, normalHours).localeCompare(this.pauseSortKey(b, normalHours)))
            .forEach(settlement => {
                if (pauseToSettle > 0) {
                    const subtract = Math.min(pauseToSettle, settlement.minutes);

                    settlement.minutes = settlement.minutes - subtract;

                    settlement.description = `${settlement.description} (${new MinutesPipe().transform(subtract)} pauze)`;

                    pauseToSettle -= subtract;

                    if (settlement.normal !== undefined) {
                        settlement.normal.minutes = settlement.minutes;
                        settlement.normal.description = settlement.description;
                    }
                }

                if (settlement.bonus !== undefined) {
                    settlement.bonus = undefined;
                }

                if (settlement.normal !== undefined) {
                    if (settlement.normal.bonus !== undefined) {
                        settlement.normal.bonus = undefined;
                    }

                    settlement.normal = undefined;
                }
            });
    }

    /**
     * Bepaalt de volgorde waarin pauze van settlements wordt afgetrokken: eerst de normale uren
     * (GU/MU en scholing), daarna de toeslaguren van laag naar hoog percentage.
     *
     * Toeslagcodes met een `V` (bijv. `130V`) tellen mee als hun percentage minus 100, zodat ze
     * vlak voor het bijbehorende volle percentage vallen.
     */
    private pauseSortKey(settlement: Settlement, normalHours: string[]) {
        /**
         * Alleen toeslagcodes als `130V`, niet de verlofcode `V` zelf: verlof is een vast aantal
         * uren en mag niet als eerste door de pauze verlaagd worden.
         */
        const isBonusCode = /^\d+V$/.test(settlement.hourtype_code);

        let key = isBonusCode
            ? `${+settlement.hourtype_code.replace(CONTRACT_HOURTYPE_CODE_LEAVE, '') - 100}`
            : settlement.hourtype_code;

        if (normalHours.includes(key) || key.includes(CONTRACT_HOURTYPE_CODE_EDUCATION)) {
            key = '1';
        }

        return key.padStart(3, '0');
    }

    async saveSettlements(
        day: WeekDay,
        employeeId: number,
        fcHourTypeMap: Map<string, string>,
        updateForm?: (settlements: Settlement[], day: WeekDay) => void,
        savedFunc?: () => unknown
    ) {
        /**
         * Proefverrekening: niets opslaan en de formulieren met rust laten.
         */
        if (this.dryRun) {
            return;
        }

        const result = await lastValueFrom(
            this.saveMulti(day.settlements, new Date(formatDate(day.date, 'yyyy-MM-dd', 'nl')), employeeId)
        );

        day.settlements = result.data;

        fcHourTypeMap.forEach(fcName => {
            day.fg.controls[fcName].reset();
            day.fgSecond.controls[fcName].reset();
        });

        day.fg.controls.supplemented.reset();

        if (updateForm) {
            updateForm(day.settlements, day);
        } else {
            console.warn('No updateForm function provided, settlements not updated in form');
        }

        if (savedFunc) {
            savedFunc();
        }
    }

    handleVrijInAfas(
        day: WeekDay,
        settlements: Settlement[],
        aanAfwezigheid: AanAfwezigheid[],
        hourTypes: Hourtype[],
        fcHourTypeMap: Map<string, string>,
        employeeId: number,
        sleepOrLeaveHourCodes: Set<string>
    ) {
        if (!Array.isArray(aanAfwezigheid)) {
            return;
        }
        // Vrij - Afas
        aanAfwezigheid
            .filter(
                leave =>
                    Utils.dateString(leave.startDate) === Utils.dateString(day.date) &&
                    (!!leave.leaveCode || !!leave.typeverzuimcode)
            )
            .forEach(aanAfw => {
                const verlofSettlements = settlements.filter(s =>
                    hourTypes
                        .filter(h => h.type === CONTRACT_HOURTYPE_CODE_VERLOF)
                        .map(h => h.code)
                        .includes(s.hourtype_code)
                );

                const verlofTreshold = verlofSettlements.map(s => s.minutes).reduce((sum, current) => sum + current, 0);

                if (verlofTreshold >= 480) {
                    return;
                }

                const settlement = new Settlement();

                settlement.user_id = employeeId;

                settlement.bookdate = formatDate(day.date, 'yyyy-MM-dd', 'nl') as any;

                settlement.minutes = aanAfw.hours;

                settlement.hourtype_code = aanAfw.leaveCode ?? aanAfw.typeverzuimcode;

                settlement.skip_pause = !!aanAfw.typeverzuimcode;

                settlement.afas_leave = true;

                if (
                    settlement.hourtype_code.substring(0, 4) === 'W202' ||
                    settlement.hourtype_code.substring(0, 4) === 'R202' ||
                    settlement.hourtype_code === 'ERVD' ||
                    settlement.hourtype_code === 'TSF'
                ) {
                    settlement.hourtype_code = CONTRACT_HOURTYPE_CODE_LEAVE;
                }

                if (['B', 'O'].includes(settlement.hourtype_code)) {
                    // Bedrijfsongeval
                    settlement.hourtype_code = CONTRACT_HOURTYPE_CODE_SICK;
                }

                if (settlement.hourtype_code === CONTRACT_HOURTYPE_CODE_SICK) {
                    const totalIllOrWorked = settlements
                        .filter(s =>
                            [CONTRACT_HOURTYPE_CODE_SICK, CONTRACT_HOURTYPE_CODE_WORK_REGULAR].includes(s.hourtype_code)
                        )
                        .map(s => s.minutes)
                        .reduce((sum, current) => sum + current, 0);

                    const toBeSettled = Math.min(480 - totalIllOrWorked, settlement.minutes);

                    settlement.minutes = Math.max(toBeSettled, 0);
                }

                settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

                settlement.description = `${aanAfw.reasonDescr}`;

                if (settlement.minutes > 0) {
                    settlements.push(settlement);

                    if (aanAfw.leaveCode) {
                        sleepOrLeaveHourCodes.add(aanAfw.leaveCode);

                        fcHourTypeMap.set(aanAfw.leaveCode, 'normal');
                    }
                }
            });
    }

    settleRealisationsForHourTypes(
        realisations: Realisation[],
        realisationHourtypes: RealisationHourtype[],
        hourtypeCode: string,
        hourTypes: Hourtype[]
    ) {
        return realisations
            .filter(r => realisationHourtypes.includes(r.hourtype))
            .map(realisation =>
                this.createSettlementFromRealisation(
                    realisation,
                    hourTypes,
                    Utils.minuteDuration(realisation.enddate, realisation.begindate),
                    hourtypeCode,
                    new Date(realisation.begindate),
                    new Date(realisation.enddate)
                )
            );
    }

    isGapMoreThanTwoHours(realisations: Realisation[]) {
        return realisations
            .sort((a, b) => Utils.getTimeOrNull(a.begindate) - Utils.getTimeOrNull(b.begindate))
            .some((realisation, i) => {
                if (i === realisations.length - 1) {
                    return false;
                }

                const endDateInMilliseconds = Utils.getTimeOrNull(realisation.enddate);
                const nextBeginDateInMilliseconds = Utils.getTimeOrNull(realisations[i + 1].begindate);

                return nextBeginDateInMilliseconds - endDateInMilliseconds >= 2 * 60 * 60 * 1000;
            });
    }

    createSettlementFromRealisation(
        realisation: Realisation,
        hourTypes: Hourtype[],
        minutes: number,
        hourType: string,
        from: Date,
        to: Date,
        subStart = 0,
        subEnd = 0
    ) {
        const settlement = new Settlement();

        settlement.user_id = realisation.user_id;
        settlement.bookdate = realisation.bookdate;
        settlement.realisation_id = realisation.id;
        settlement.realisation = realisation;

        settlement.minutes = minutes;

        settlement.hourtype_code = hourType;
        settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

        from = new Date(from);

        to = new Date(to);

        if (subStart) {
            from.setMinutes(from.getMinutes() + subStart);
        }

        if (subEnd) {
            to.setMinutes(to.getMinutes() - subEnd);
        }

        settlement.description = `${formatDate(from, 'EE d MMM yyyy HH:mm', 'nl')} - `;

        if (from.getDate() === to.getDate()) {
            settlement.description += `${formatDate(to, 'HH:mm', 'nl')}`;
        } else {
            settlement.description += `${formatDate(to, 'EE d MMM yyyy HH:mm', 'nl')}`;
        }

        if (realisation.hourtype === RealisationHourtype.sleep) {
            settlement.description = `Slaapdag ${settlement.description}`;
        }

        if (
            [RealisationHourtype.travel_to, RealisationHourtype.travel_back].includes(realisation.hourtype) &&
            realisation.text_prepend &&
            realisation.text_append
        ) {
            settlement.description += ` van ${realisation.text_prepend} naar ${realisation.text_append}`;
        }

        return settlement;
    }

    settleVerblijfskosten(
        day: WeekDay,
        employee: User,
        hasCaoBouwInfra: boolean,
        overtimeHourTypeCodes: string[],
        hourTypes: Hourtype[]
    ) {
        const verblijfskostenSettlements: Settlement[] = [];

        if (hasCaoBouwInfra) {
            return verblijfskostenSettlements;
        }

        const workSettlements = day.settlements.filter(s =>
            [...CONTRACT_HOURTYPE_CODES_WORK, ...overtimeHourTypeCodes].includes(s.hourtype_code)
        );

        const totalMinutesDay =
            workSettlements.map(s => s.minutes).reduce((sum, current) => sum + current, 0) + day.fg.value.pause;

        if (totalMinutesDay > 240) {
            const firstRealisation = day.realisations
                .filter(r =>
                    [
                        RealisationHourtype.worktime,
                        RealisationHourtype.driving_back,
                        RealisationHourtype.driving_to,
                    ].includes(r.hourtype)
                )
                .sort((a, b) => Utils.getTimeOrNull(a.enddate) - Utils.getTimeOrNull(b.enddate))[0];

            let totalMinutesVBK01 = 0;
            let totalMinutesVBK05 = 0;
            let totalMinutesVBK10 = 0;

            const realisations = day.realisations.filter(r =>
                [
                    RealisationHourtype.worktime,
                    RealisationHourtype.driving_back,
                    RealisationHourtype.driving_to,
                ].includes(r.hourtype)
            );

            realisations.forEach(realisation => {
                const beginDate = new Date(realisation.begindate);
                const endDate = new Date(realisation.enddate);

                let minutesVBK05 = 0;

                const before1400Hours = new Date(firstRealisation.begindate).getHours() < 14;

                if (before1400Hours) {
                    const datesToUse = [endDate];

                    if (beginDate.getDate() !== endDate.getDate()) {
                        datesToUse.push(beginDate);
                    }

                    datesToUse.forEach(dateToUse => {
                        const startVK05 = Utils.setTime(new Date(dateToUse), 18, 0);

                        const endVK05 = Utils.setTime(new Date(dateToUse), 24, 0);

                        if (beginDate.getTime() < endVK05.getTime() && endDate.getTime() > startVK05.getTime()) {
                            const calcFrom = beginDate.getTime() < startVK05.getTime() ? startVK05 : beginDate;

                            const calcUntil = endDate.getTime() > endVK05.getTime() ? endVK05 : endDate;

                            minutesVBK05 = Utils.minuteDuration(calcUntil, calcFrom);
                        }

                        totalMinutesVBK05 += minutesVBK05;
                    });
                }

                totalMinutesVBK01 += Utils.minuteDuration(endDate, beginDate) - minutesVBK05;
            });

            if (new Date(firstRealisation.begindate).getHours() >= 14 && totalMinutesDay >= 720) {
                totalMinutesVBK10 = 60;
            }

            if (totalMinutesVBK01) {
                verblijfskostenSettlements.push(
                    this.accommodationCostsSettlement(day, employee, hourTypes, totalMinutesVBK01, 'VBK01')
                );
            }

            if (totalMinutesVBK05) {
                verblijfskostenSettlements.push(
                    this.accommodationCostsSettlement(day, employee, hourTypes, totalMinutesVBK05, 'VBK05')
                );
            }

            if (totalMinutesVBK10) {
                verblijfskostenSettlements.push(
                    this.accommodationCostsSettlement(day, employee, hourTypes, totalMinutesVBK10, 'VBK10')
                );
            }
        }

        return verblijfskostenSettlements;
    }

    async settleTravelTime(day: WeekDay, employee: User, hourTypes: Hourtype[]) {
        const travelSettlements: Settlement[] = [];

        const employeeHasTravelTime =
            employee.traveltime_workday === 'Staffel' &&
            day.date.getDay() > 0 &&
            day.date.getDay() < 6 &&
            day.fg.value.hoursToReward > 0;

        if (!employeeHasTravelTime) {
            return travelSettlements;
        }

        const [travelTo, travelFrom, data] = await lastValueFrom(this.getRealisationTravelData(day, employee));

        if (!travelTo) {
            return travelSettlements;
        }

        console.warn('travelTime', { employeeHasTravelTime, travelTo, travelFrom, data });

        const highestResult = travelTo.data?.kms > travelFrom.data?.kms ? travelTo.data : travelFrom.data;

        if (!!highestResult) {
            const minutes = this.calculateTravelTimeFromStaffelKms(highestResult.kms, employee);

            console.warn(1, {
                minutes,
                from: highestResult.from,
                to: highestResult.to,
                kms: highestResult.kms,
            });

            // When any travelRealisation, don't register staffel kms
            if (minutes && !day.fg.value.travelrealisations) {
                travelSettlements.push(
                    this.travelSettlement(
                        day,
                        employee,
                        hourTypes,
                        minutes,
                        highestResult.from,
                        highestResult.to,
                        highestResult.kms
                    )
                );
            }
        } else {
            console.warn(2);

            const addresses: string[] = [];

            if (!travelTo.data) {
                addresses.push(origin + '<br>' + data.workBegin + '<br>');
            }

            if (!travelFrom.data) {
                addresses.push(data.workEnd + '<br>' + data.destination + '<br>');
            }

            let reference: string;

            if (data.realisationStart.user_planning) {
                reference = `Personeelsplanning of ingepland personeel: #${data.realisationStart.user_planning.id}`;
            } else if (data.realisationStart.planning_has) {
                reference = `Transportplanning: #${data.realisationStart.planning_has.id}`;
            } else if (data.realisationStart.planning?.location) {
                reference = `Asfaltplanning: #${data.realisationStart.planning.id}`;
            } else {
                reference = `Project (via personeelsplanning): #${data.realisationStart.planning?.afas_project_id}`;
            }

            console.error('No route for', {
                realisationStart: data.realisationStart,
                realisationEnd: data.realisationEnd,
                origin: data.origin,
                workBegin: data.workBegin,
                workEnd: data.workEnd,
                destination: data.destination,
            });

            this.confirmDialogService.confirm({
                title: 'Kon geen kilometers berekenen',
                text:
                    'Er lijkt iets mis te zijn met een van de volgende adressen:<br>' +
                    addresses.join('<br>') +
                    `<br><br>Datum: ${formatDate(data.realisationStart.begindate, 'yyyy-MM-dd HH:mm', 'nl')}` +
                    `<br>Gebruiker: ${employee.name} #${employee.id}<br>` +
                    reference,
                confirm: 'Sluiten',
                cancel: null,
            });
        }

        return travelSettlements;
    }

    async settleTravelCosts(day: WeekDay, hasCaoBouwInfra: boolean, employee: User, hourTypes: Hourtype[]) {
        const travelCostSettlements: Settlement[] = [];

        if (hasCaoBouwInfra) {
            return travelCostSettlements;
        }

        const [travelTo, travelFrom, data] = await lastValueFrom(this.getRealisationTravelData(day, employee));

        if (!travelTo?.data) {
            return travelCostSettlements;
        }

        const kms = Math.min(
            Math.ceil(((travelTo.data?.kms ?? 0) + (travelFrom.data?.kms ?? 0)) * 100) / 100,
            employee.maxkm
        );

        if (kms > 0 && !day.fg.value.travelrealisations) {
            travelCostSettlements.push(
                this.travelcostSettlement(
                    day,
                    employee,
                    hourTypes,
                    kms,
                    data.origin,
                    data.workBegin,
                    data.workEnd,
                    data.destination
                )
            );
        }

        return travelCostSettlements;
    }

    private accommodationCostsSettlement(
        day: WeekDay,
        employee: User,
        hourTypes: Hourtype[],
        minutes: number,
        hourtypeCode: string
    ) {
        const settlement = new Settlement();

        settlement.afas_project_id = this.getLargestSettlementProject(day.settlements, day.realisations);

        settlement.user_id = employee.id;

        settlement.bookdate = formatDate(day.date, 'yyyy-MM-dd', 'nl') as any;

        settlement.minutes = minutes;
        settlement.hourtype_code = hourtypeCode;

        settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

        settlement.description = `Verblijfskostenvergoeding`;

        return settlement;
    }

    private getRealisationTravelData(day: WeekDay, employee: User, useCompanyAddress = false) {
        const companyAdress = this.locationService.formatAddress(environment.companyAddress);
        const userAdress = `${employee.zipcode}, ${employee.town || ''}`;

        const realisationStart = day.realisations.find(r =>
            [RealisationHourtype.worktime, RealisationHourtype.education].includes(r.hourtype)
        );

        const prepareAdress = (addr: string) => {
            addr = addr?.trim();
            return addr?.length > 3 ? addr : null;
        };

        if (!realisationStart) {
            return of([]);
        }

        if (realisationStart.planning?.location && !/\w+/g.test(realisationStart.planning?.location)) {
            realisationStart.planning.location = null;
        }

        const phTransportOrigin = realisationStart?.planning_has?.transport_origin;

        const phTransportWorkBegin = realisationStart?.planning_has?.transport_worksite;

        const phLocationStart =
            (phTransportOrigin === 'none' ? null : phTransportOrigin) ??
            (phTransportWorkBegin ? phTransportWorkBegin : null);

        let workBegin =
            prepareAdress(phLocationStart) ??
            prepareAdress(realisationStart.project?.location) ??
            prepareAdress(realisationStart?.user_planning?.work_begin) ??
            prepareAdress(realisationStart?.user_planning?.project?.location) ??
            prepareAdress(realisationStart.planning?.location) ??
            prepareAdress(realisationStart.planning_has?.project?.location) ??
            companyAdress;

        if (useCompanyAddress) {
            workBegin = companyAdress;
        }

        const realisationEnd = [...day.realisations]
            .reverse()
            .find(r => [RealisationHourtype.worktime, RealisationHourtype.education].includes(r.hourtype));

        const phTransportDestination = realisationEnd?.planning_has?.transport_destination;
        const phTransportWorkEnd = realisationStart?.planning_has?.transport_worksite_end;

        const phLocationEnd =
            (phTransportDestination === 'none' ? null : phTransportDestination) ??
            (phTransportWorkEnd ? phTransportWorkEnd : null);

        let workEnd =
            prepareAdress(phLocationEnd) ??
            prepareAdress(realisationEnd.project?.location) ??
            prepareAdress(realisationEnd.user_planning?.work_end) ??
            prepareAdress(realisationEnd.user_planning?.project?.location) ??
            prepareAdress(realisationEnd.planning?.location) ??
            prepareAdress(realisationEnd.planning_has?.project?.location) ??
            companyAdress;

        /**
         * `user_planning.origin`/`.destination` krijgen bij het aanmaken automatisch het
         * bedrijfsadres, ook voor medewerkers die vanaf huis vertrekken. Voor hen valt het terug op
         * het adres van de medewerker; wie `default_stand` op het bedrijfsadres heeft blijft staan.
         */
        const isCompanyAddress = (addr: string) => !!addr && addr.includes(environment.companyAddress.street);

        const ignoreCompanyAddress = employee.default_stand !== DefaultStand.Floridadreef;

        const upDestination = realisationEnd?.user_planning?.destination;
        let destination =
            (ignoreCompanyAddress && isCompanyAddress(upDestination) ? null : upDestination) ?? userAdress;

        const upOrigin = realisationStart?.user_planning?.origin;
        let origin = (ignoreCompanyAddress && isCompanyAddress(upOrigin) ? null : upOrigin) ?? userAdress;
        // correction to prevent some km's if chauffeur is set to from home in transportplanning
        if (destination.includes(employee.zipcode)) {
            destination = `${employee.street} ${employee.housenumber}, ${employee.zipcode} ${employee.town}`;
        }
        if (origin.includes(employee.zipcode)) {
            origin = `${employee.street} ${employee.housenumber}, ${employee.zipcode} ${employee.town}`;
        }

        if (useCompanyAddress) {
            workEnd = companyAdress;
        }

        const workBegin$ = workBegin
            ? this.locationService.autoRoute(origin, workBegin)
            : of({ data: { kms: 0, from: '', to: '' } });

        const workEnd$ = workEnd
            ? this.locationService.autoRoute(workEnd, destination)
            : of({ data: { kms: 0, from: '', to: '' } });

        return combineLatest([
            workBegin$,
            workEnd$,
            of({ origin, workEnd, workBegin, destination, realisationStart, realisationEnd }),
        ]);
    }

    private calculateTravelTimeFromStaffelKms(kms: number, employee: User) {
        const oneHourMinutes = 60;

        if (this.employeeService.hasAsphaltFunction(employee)) {
            if ((kms >= 30 && kms < 51) || kms >= 106) {
                const refSpeed = 60; // op basis van 60km/h
                return oneHourMinutes * (kms / refSpeed);
            }

            if (kms >= 51 && kms < 60) {
                return oneHourMinutes;
            }

            if (kms >= 60 && kms < 71) {
                return oneHourMinutes * 1.2;
            }

            if (kms >= 71 && kms < 82) {
                return oneHourMinutes * 1.4;
            }

            if (kms >= 82 && kms < 93) {
                return oneHourMinutes * 1.5;
            }

            if (kms >= 93 && kms < 106) {
                return oneHourMinutes * 1.8;
            }
        } else {
            if (kms >= 120) {
                return oneHourMinutes * 2;
            }

            if (kms >= 70) {
                return oneHourMinutes;
            }
        }

        return 0;
    }

    private travelcostSettlement(
        day: WeekDay,
        employee: User,
        hourTypes: Hourtype[],
        kms: number,
        from: string,
        to: string,
        backFrom: string,
        backTo: string
    ) {
        const settlement = new Settlement();

        settlement.user_id = employee.id;

        settlement.bookdate = formatDate(day.date, 'yyyy-MM-dd', 'nl') as any;

        settlement.minutes = kms;
        settlement.amount = kms.toString();
        settlement.hourtype_code = 'CRW';

        settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

        settlement.description = `Reiskosten van ${from} naar ${to} en van ${backFrom} naar ${backTo} (${formatNumber(kms, 'nl')} km)`;

        return settlement;
    }

    private travelSettlement(
        day: WeekDay,
        employee: User,
        hourTypes: Hourtype[],
        minutes: number,
        from: string,
        to: string,
        kms: number
    ) {
        const settlement = new Settlement();

        settlement.afas_project_id = this.getLargestSettlementProject(day.settlements, day.realisations);

        settlement.user_id = employee.id;

        settlement.bookdate = formatDate(day.date, 'yyyy-MM-dd', 'nl') as any;

        settlement.minutes = minutes;

        settlement.hourtype_code = this.employeeService.hasCaoBouwInfra(employee)
            ? SettlementHourtype.travelHoursActual
            : SettlementHourtype.travelHours;

        settlement.hourtype = hourTypes.find(h => h.code === settlement.hourtype_code);

        settlement.description = `Reisuren van ${from} naar ${to} (${Math.ceil(kms)} km)`;

        return settlement;
    }
}

export class SettlementCounts {
    afas_project_id: string;
    settlement_count: number;
    project_name: string;
    error_count: number;
    sent_count: number;

    retrying?: number;
}
